import {
  AuthService,
  ExchangeTokenResponse,
  RefreshTokenResponse,
  VerifyAuthResponse,
} from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import {
  LoginFeatures,
  GetLoginFeaturesResponse,
} from '@weave/schema-gen-ts/dist/schemas/login-features/v1/service.pb';
import {
  AuthConfig,
  AuthStorage,
  OidcClientConfig,
  localStorageHelper,
  PortalUser,
  SignInMethod,
  getDecodedWeaveToken,
  getWeaveToken,
  setWeaveToken,
  getExpireLength,
  getJTI,
  deleteWeaveSessionId,
  setUser,
  getUser,
  clearStoredAuthData,
} from '@frontend/auth-helpers';
import appConfig, { getInitialParams } from '@frontend/env';
import { shell } from '@frontend/shell-utils';
import { sentry } from '@frontend/tracking';
import { AuthFlow, SignInProps, SignOutProps, ClientTokens, HandleCallbackProps } from './AuthFlow';
import { APIFetchNoAuth, AuthorizedFetch } from './fetch';
import { LegacyClient } from './legacy.class';
import { OidcClient } from './oidc.class';
import { OktaClient } from './okta.class';

const oidc_code_hint = 'ory_ac_';
const { oktaId, backendApi, oidcIssuer, oidcClientID } = getInitialParams();
const appSource = appConfig.APP_SOURCE || 'weave2';
const oktaIssuer = appConfig.OKTA_ISSUER || 'https://auth.weaveconnect.com/oauth2/default';

// NOTE this the redirect uri is in this getter function to mitigate subtle timing and env issues in our tests that cause flake
function getRedirectUri() {
  return `${window.location.origin}${appConfig.BASE_PATH ? `/${appConfig.BASE_PATH}` : ''}/sign-in/callback`;
}

export class Authn {
  private static instance: Authn;
  private authMethods: SignInMethod[] = ['okta', 'oidc', 'legacy'];
  private currentAuthMethod: SignInMethod = 'oidc';
  private hasCheckedAuthMethod = false;
  // These get defined in the constructor
  private oidcClient: OidcClient | undefined;
  private oktaClient: OktaClient | undefined;
  private legacyClient: AuthFlow | undefined;
  private config: AuthConfig = {};
  // Store the refresh token promise at the class level
  private refreshTokenPromise: Promise<RefreshTokenResponse> | null = null;

  private constructor() {
    this.setConfig(this.config);
  }

  static getInstance(): Authn {
    if (!Authn.instance) {
      Authn.instance = new Authn();
    }

    return Authn.instance;
  }

  public setConfig(incomingConfig: AuthConfig): void {
    this.config.oktaIssuer = incomingConfig.oktaIssuer || oktaIssuer;
    this.config.oktaClientID = incomingConfig.oktaClientID || oktaId;
    this.config.oktaRedirectUri = incomingConfig.oktaRedirectUri || getRedirectUri();
    this.config.oidcIssuer = incomingConfig.oidcIssuer || oidcIssuer;
    this.config.oidcClientID = incomingConfig.oidcClientID || oidcClientID;
    this.config.oidcRedirectUri = incomingConfig.oidcRedirectUri || getRedirectUri();
    this.config.backendAPI = incomingConfig.backendAPI || backendApi;
    this.config.appSource = incomingConfig.appSource || appSource;
    this.initializeValues();
  }

  private initializeValues(): void {
    if (!this.config.oidcIssuer) {
      this.authMethods = ['okta', 'legacy'];
    } else {
      const opts: OidcClientConfig = {
        issuer: new URL(this.config.oidcIssuer),
        scopes: ['openid', 'email'],
      };

      if (!!this.config.oidcClientID) {
        opts.clientID = this.config.oidcClientID;
        opts.redirectUri = new URL(this.config.oidcRedirectUri || '');
      }

      this.oidcClient = new OidcClient(opts);
    }

    this.oktaClient = new OktaClient({
      issuer: this.config.oktaIssuer || '',
      clientId: this.config.oktaClientID || '',
      redirectUri: this.config.oktaRedirectUri || '',
      scopes: ['openid', 'email', 'profile'],
    });
    this.legacyClient = new LegacyClient();
  }

  /**
   * getSignInMethod is a method that checks the current auth method and returns it.
   * It also checks the loginFeatures service to establish some features that are not associated
   * with specific locations or organizations.
   */
  public async getSignInMethod(): Promise<SignInMethod> {
    if (this.hasCheckedAuthMethod) {
      return Promise.resolve(this.currentAuthMethod);
    }
    // if we are in preview, we should always default to legacy auth
    // TODO: preview.weavelab.xyz is the old way of serving previews. This will be going away soon
    // f.weavedev.net will be replacing preview.weavelab.xyz
    const origin = window.location.origin;
    if (
      origin.includes('preview.weavelab.xyz') ||
      origin.includes('f.weavedev.net') ||
      origin.includes('edge.getweave.com') ||
      localStorageHelper.get('backendEnv') === 'dev'
    ) {
      this.hasCheckedAuthMethod = true;
      this.currentAuthMethod = 'legacy';
      return Promise.resolve('legacy');
    }
    const lastSignInMethod = localStorageHelper.get(AuthStorage.sign_in_method);
    if (
      (!!lastSignInMethod && lastSignInMethod === 'okta') ||
      lastSignInMethod === 'legacy' ||
      lastSignInMethod === 'oidc'
    ) {
      this.hasCheckedAuthMethod = true;
      this.currentAuthMethod = lastSignInMethod;
      return Promise.resolve(lastSignInMethod);
    }
    return this.getLoginFeatures()
      .then((features: string[]) => {
        if (features.length === 0) {
          return 'okta';
        }
        this.hasCheckedAuthMethod = true;
        this.currentAuthMethod = features.includes('oidc') ? 'oidc' : features.includes('okta') ? 'okta' : 'legacy';
        return this.currentAuthMethod;
      })
      .catch(() => {
        return 'oidc'; // default for prod until OIDC is released
      });
  }

  public changeAuthMethod(authMethod?: SignInMethod): SignInMethod {
    if (authMethod === 'okta') {
      // start loading @okta/okta-auth-js module ahead of time
      this.oktaClient?.initializeOkta();
    }
    if (!!authMethod) {
      localStorageHelper.create(AuthStorage.sign_in_method, authMethod);
      this.currentAuthMethod = authMethod;
      return authMethod;
    }

    // if no auth method is passed in, cycle through the available auth methods
    const currentMethodIndex = this.authMethods.indexOf(this.currentAuthMethod);
    const nextMethodIndex = currentMethodIndex === this.authMethods.length - 1 ? 0 : currentMethodIndex + 1;
    const currentSignInMethod: SignInMethod = this.authMethods[nextMethodIndex];
    localStorageHelper.create(AuthStorage.sign_in_method, currentSignInMethod);
    this.currentAuthMethod = currentSignInMethod;
    return currentSignInMethod;
  }

  public getCurrentAuthMethod(): SignInMethod {
    return this.currentAuthMethod;
  }

  public async fetchUser() {
    const token = getJTI();
    const decodedToken = getDecodedWeaveToken();
    if (!decodedToken) {
      throw new Error('No token found');
    }
    const options: RequestInit = {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    };

    const fetchUser = async () => fetch(`${this.config.backendAPI}/portal/users/${decodedToken.user_id}`, options);

    try {
      const resp = await fetchUser().then(async (firstResp) => {
        if (firstResp.status >= 500) {
          // Add small delay and retry
          await new Promise((resolve) => setTimeout(resolve, 250));
          return fetchUser();
        }
        return firstResp;
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch user: ${resp.status}`);
      }
      const json: { data: PortalUser } = await resp.json();
      if (!json.data) {
        console.error('User Not Found');
        return undefined;
      }

      setUser(json.data);
      return json.data;
    } catch (err) {
      console.error(err);
      return undefined;
    }
  }

  /**
   * Does the `user` entry match the user ID in the decoded weave token?
   */
  public storedUserIsLoggedInUser(): boolean {
    const userInLS = getUser();
    const decodedToken = getDecodedWeaveToken();
    if (!userInLS || !decodedToken) return true; // can't verify, assume true
    return userInLS.userID === decodedToken.user_id;
  }

  /*
   * shouldUseJTI is a method that checks if the application should use JTI.
   * It will return true if the login features include 'jti'.
   * If the login features are not stored in local storage, it will fetch them.
   */
  public async shouldUseJTI(): Promise<boolean> {
    let useJti = false;
    const loginFeatures = await this.getLoginFeatures();
    if (loginFeatures.length > 0) {
      useJti = loginFeatures.includes('jti');
    }
    return useJti;
  }

  /*
   * getLoginFeatures is a method that fetches the login features from the login features service.
   * It will store the features in local storage and return them.
   * If the features are already stored in local storage, it will return them.
   */
  private async getLoginFeatures(): Promise<string[]> {
    const lastChecked = localStorage.getItem(AuthStorage.login_features_checked);
    const storedLoginFeatures = localStorage.getItem(AuthStorage.login_features);
    const now = Date.now();

    if (lastChecked && storedLoginFeatures) {
      const lastCheckedTime = parseInt(lastChecked, 10);
      if (now - lastCheckedTime < 10 * 60 * 1000) {
        // 10 minutes
        // if (now - lastCheckedTime < 24 * 60 * 60 * 1000) { // 24 hours
        try {
          return JSON.parse(storedLoginFeatures) as string[];
        } catch {
          // fall-through to fetch new features
          // clear the login features if it fails to parse
          localStorage.removeItem(AuthStorage.login_features);
          localStorage.removeItem(AuthStorage.login_features_checked);
        }
      }
    }

    const fingerprint = localStorageHelper.get(AuthStorage.sign_in_fingerprint);
    const res = await LoginFeatures.GetLoginFeatures(
      APIFetchNoAuth<GetLoginFeaturesResponse>(this.config.backendAPI as string),
      { fingerprint }
    );

    if (res.fingerprint) {
      localStorageHelper.create(AuthStorage.sign_in_fingerprint, res.fingerprint);
    }
    localStorageHelper.create(AuthStorage.login_features, JSON.stringify(res.features));
    localStorageHelper.create(AuthStorage.login_features_checked, `${now}`);

    return res.features || [];
  }

  private getAuthClient(): AuthFlow {
    let authClient: AuthFlow | undefined;

    switch (this.currentAuthMethod) {
      case 'oidc':
        authClient = this.oidcClient;
        break;
      case 'okta':
        authClient = this.oktaClient;
        break;
      case 'legacy':
        authClient = this.legacyClient;
        break;
      default:
        if (this.authMethods.includes('oidc')) {
          // depending on the mode we should default to oidc or legacy
          authClient = this.oidcClient;
        } else {
          authClient = this.legacyClient;
        }
        break;
    }

    if (!authClient) {
      throw new Error(`Tried to get auth client for auth method ${this.currentAuthMethod} but it was not defined`);
    }

    return authClient;
  }

  public async exchangeToken(tokens: ClientTokens, source?: string): Promise<string> {
    try {
      const clientSource = source ?? this.config.appSource;
      const res = await AuthService.ExchangeToken(
        APIFetchNoAuth<ExchangeTokenResponse>(this.config.backendAPI as string),
        {
          tokenId: tokens.IDToken,
          expiration: `${getExpireLength()}`, // defaults to 4h but can be overridden by localstorage
          source: clientSource,
        }
      );
      if (res.token) {
        setWeaveToken(res.token, res.jtiToken || '');
        return res.token;
      } else {
        throw new Error('No token returned');
      }
    } catch (err) {
      sentry.error({
        topic: 'auth',
        team: 'users-infra',
        error: err,
        addContext: {
          name: 'auth',
          context: {
            errMessage: 'OIDC Login Token Exchange',
          },
        },
      });
      throw err;
    }
  }

  public async refreshWeaveToken(): Promise<RefreshTokenResponse> {
    // NOTE if try throws, the finally block will still execute, clearing the promise
    // and the error will be thrown to the caller
    try {
      // If there's already a refresh in progress, return the existing promise
      if (this.refreshTokenPromise) {
        return await this.refreshTokenPromise;
      }

      this.refreshTokenPromise = this.doRefreshToken();

      return await this.refreshTokenPromise;
    } finally {
      // Clear the promise once it's resolved or rejected
      this.refreshTokenPromise = null;
    }
  }

  private async doRefreshToken(): Promise<RefreshTokenResponse> {
    let token = getWeaveToken();
    const useJTI = await this.shouldUseJTI();
    if (useJTI) {
      token = getJTI();
    }
    if (!token) {
      throw new Error('No existing weave token');
    }
    const res = await AuthService.RefreshToken(AuthorizedFetch(this.config.backendAPI as string, token), {});
    if (!!res.token) {
      setWeaveToken(res.token, res.jtiToken || '');
      return res;
    }
    throw new Error('Refresh token failed');
  }

  public getTokens(): Promise<ClientTokens> {
    return this.getAuthClient().getTokens();
  }

  public async handleCallback(props?: HandleCallbackProps): Promise<ClientTokens> {
    // based on the code in the query params we should identify which auth method to use
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get('code');
    if (code && code.indexOf(oidc_code_hint) != -1) {
      this.changeAuthMethod('oidc');
    }
    return this.getAuthClient()
      .handleCallback(props)
      .then((tokens) => {
        if (tokens?.IDToken) {
          return this.exchangeToken(tokens, props?.clientId).then((weaveJWT: string) => {
            if (weaveJWT) {
              tokens.weaveToken = weaveJWT;
            }
            return tokens;
          });
        }
        return {};
      });
  }

  public isUserAuthenticated(): boolean {
    return this.getAuthClient().isUserAuthenticated();
  }

  public signIn(props?: SignInProps): Promise<VerifyAuthResponse> {
    return this.getAuthClient().signIn(props);
  }

  // signOut will result in a browser redirect, nothing after promise resolves can be guaranteed to run
  public async signOut(props?: SignOutProps): Promise<void> {
    clearStoredAuthData();
    deleteWeaveSessionId();

    // When running inside the desktop shell with browser auth, skip the auth strategy's
    // signOut — it would redirect the webContents to an external OIDC/Okta endpoint,
    // racing with the shell's own login-page navigation. Instead, tell the shell to
    // handle logout cleanup (clear tokens, show login page) via IPC.
    if (shell.isShell && shell.featureAvailability?.has('desktop-browser-auth')) {
      localStorageHelper.delete(AuthStorage.sign_in_method); // remove the stored sign in method before navigating away
      shell.emit?.('auth:clear-stored-auth-data', undefined);
      return;
    }

    await this.getSignInMethod(); // ensure we're signing out with the correct auth client
    localStorageHelper.delete(AuthStorage.sign_in_method); // remove the stored sign in method before navigating away
    return this.getAuthClient().signOut(props);
  }

  public async frontChannelLogout(): Promise<void> {
    clearStoredAuthData();
    deleteWeaveSessionId();
    localStorageHelper.delete(AuthStorage.sign_in_method);
  }
}

export const authnClient = Authn.getInstance();

// Listen for changes to the weave token from other tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === AuthStorage.weave_token) {
      console.log('Weave token changed in another tab');
      if (!e.newValue) {
        setTimeout(() => {
          authnClient.frontChannelLogout();
          window.location.reload();
        }, 2000);
      }
    }
  });
}
